import { useState, useEffect, useMemo, useCallback } from "react";
import {
  BarChart2,
  Download,
  Bell,
  Search,
  SlidersHorizontal,
  Table2,
  LayoutGrid,
  User,
  UserCheck,
  Store,
  Banknote,
  CreditCard,
  Smartphone,
  Landmark,
  ScanLine,
  Check,
  Clock,
  Package,
  CornerUpLeft,
  Wallet,
  TrendingUp,
  MapPin,
  Star,
  BadgeCheck,
  Printer,
  ChevronDown,
  ChevronsUpDown,
  Split,
  CheckCircle,
  ClockArrowUp,
  PackageCheck,
  Undo2,
  X,
  ChevronLeft,
  ChevronRight,
  CircleDot,
} from "lucide-react";
import "./Transactions.css";
import Template from "../Template";
import { uiAlert } from "../../components/alerts/customAlert/uiAlert";
import {
  clearLoanUrl,
  fetchUsersTransactionUrl,
  extendLoanUrl,
  splitPaymentUrl,
  returnAllProductUrl,
  markSuppliedUrl,
  supplyAllProductUrl,
  returnOneProductUrl,
  pushPaymentKudaUrl,
} from "../../api/endpoints";
import { useSelector } from "react-redux";
import { Pagination } from "../productCatelog/ProductCatelog";
import { truncateID } from "../stores/makeTansaction/CashTransactionComp";
import TransactionsSkeleton from "./TransactionSkeletion";
import { useNavigate } from "react-router-dom";
import InvoicePDF from "../stores/makeTansaction/printTest/GeneratePDF";
import ClearLoanModal from "../../components/transactionDetailAction/CLearLoan";
import ExtendLoanModal from "../../components/transactionDetailAction/ExtendDueDates";
import RegisterPaymentModal from "../../components/transactionDetailAction/SplitPayment";
import { ButtonLoader, ButtonLoaderII } from "../../components/uxCollections/Loaders";
import Loader from "../../components/uxCollections/storelensSpiner/StorelenseSpinner";

/* ─────────────────── helpers ─────────────────── */
const fmt = (n, currency = "#") => currency + Math.round(n).toLocaleString("en-NG");
export const fmtC = (n, currency = "#") => {
  if (n >= 1e9) return currency + (n / 1e9).toFixed(1) + "B";
  if (n >= 1e6) return currency + (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return currency + (n / 1e3).toFixed(1) + "K";
  return fmt(n, currency);
};

// Returns [start, end] Date bounds (inclusive) for a given period label, or null for "All time"
function getDateRangeForPeriod(period) {
  console.log("getting date range for period:", period);

  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

  switch (period) {
    case "Today": {
      const s = startOfDay(now);
      return [s, endOfDay(now)];
    }
    case "Yesterday": {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      return [startOfDay(y), endOfDay(y)];
    }
    case "This week": {
      const s = new Date(now);
      const dow = (s.getDay() + 6) % 7; // Monday = 0
      s.setDate(s.getDate() - dow);
      return [startOfDay(s), endOfDay(now)];
    }
    case "This month": {
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      return [startOfDay(s), endOfDay(now)];
    }
    case "Last month": {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 0);
      return [startOfDay(s), endOfDay(e)];
    }
    case "This quarter": {
      const q = Math.floor(now.getMonth() / 3);
      const s = new Date(now.getFullYear(), q * 3, 1);
      return [startOfDay(s), endOfDay(now)];
    }
    case "This year": {
      const s = new Date(now.getFullYear(), 0, 1);
      return [startOfDay(s), endOfDay(now)];
    }
    case "All time":
    default:
      return null;
  }
}

const AVATAR_PALETTES = [
  ["#dbeafe", "#1e40af"],
  ["#fce7f3", "#9d174d"],
  ["#d1fae5", "#065f46"],
  ["#fef3c7", "#78350f"],
  ["#ede9fe", "#4c1d95"],
  ["#fee2e2", "#991b1b"],
  ["#e0f2fe", "#0c4a6e"],
];
const avatarColors = (name) => AVATAR_PALETTES[(name?.charCodeAt(0) || 65) % AVATAR_PALETTES.length];

const PERIODS = ["Today", "Yesterday", "This week", "This month", "Last month", "This quarter", "This year", "All time"];

/* ─────────────────── mock data ─────────────────── */
const STORES = {
  s1: {
    storeName: "Pragma Electronics — Ikeja",
    storeAddress: "14 Allen Avenue, Ikeja, Lagos",
    category: "Electronics",
    superMarketMode: false,
    isStoreVerified: true,
    isVATenabled: true,
    vatPercentage: 7.5,
    currency: "NGN",
    targetSales: 2000000,
    targetProfit: 400000,
    averageRating: 4.7,
    numberOfFollowers: 1240,
  },
  s2: {
    storeName: "StoreLense Hub — Lekki",
    storeAddress: "3 Admiralty Way, Lekki Phase 1, Lagos",
    category: "General Retail",
    superMarketMode: true,
    isStoreVerified: true,
    isVATenabled: false,
    vatPercentage: 0,
    currency: "NGN",
    targetSales: 3500000,
    targetProfit: 700000,
    averageRating: 4.5,
    numberOfFollowers: 2100,
  },
  s3: {
    storeName: "Pragma Superstore — Abuja",
    storeAddress: "7 Wuse Zone 5, Abuja FCT",
    category: "Supermarket",
    superMarketMode: true,
    isStoreVerified: false,
    isVATenabled: false,
    vatPercentage: 0,
    currency: "NGN",
    targetSales: 5000000,
    targetProfit: 900000,
    averageRating: 4.2,
    numberOfFollowers: 870,
  },
};

const TX_DATA = [
  {
    transID: "14062026_A1B2C",
    transactionDetails: {
      date: new Date("2025-06-15T09:22:00"),
      deliveryStatus: "supplied",
      paymentMode: "cash",
      numberOfItems: 3,
      subTotal: 96000,
      totalAmount: 100200,
      discount: 2000,
      vat: 6200,
      vatPercentage: 7.5,
      serviceCharge: 0,
      deliveryFee: 0,
    },
    soldBy: { fullName: "Chisom Adaeze" },
    customer: { fullName: "Emeka Okafor", email: "emeka@gmail.com", phoneNumber: "0803 123 4567", address: "22 Opebi Road, Ikeja" },
    store: { id: "s1", name: "Pragma Electronics — Ikeja" },
    operationType: { whichOperation: "cash" },
    products: [
      {
        name: "Samsung Galaxy A54",
        productImage: "📱",
        soldUnitPrice: 89000,
        productCostPrice: 72000,
        quantity: 1,
        totalAmount: 89000,
        category: "Phones",
        returned: false,
      },
      {
        name: "Protective Case (×2)",
        productImage: "🛡️",
        soldUnitPrice: 3500,
        productCostPrice: 1200,
        quantity: 2,
        totalAmount: 7000,
        category: "Accessories",
        returned: false,
      },
    ],
    comments: null,
  },
  {
    transID: "14062026_D3E4F",
    transactionDetails: {
      date: new Date("2025-06-15T11:40:00"),
      deliveryStatus: "pending",
      paymentMode: "mobilePay",
      numberOfItems: 1,
      subTotal: 120000,
      totalAmount: 125000,
      discount: 0,
      vat: 0,
      vatPercentage: 0,
      serviceCharge: 0,
      deliveryFee: 5000,
    },
    soldBy: { fullName: "Tunde Bello" },
    customer: { fullName: "Ngozi Eze", email: "ngozi.eze@yahoo.com", phoneNumber: "0905 876 5432", address: "Lekki Phase 1, Lagos" },
    store: { id: "s2", name: "StoreLense Hub — Lekki" },
    operationType: { whichOperation: "loan", intialPayment: 50000, balance: 75000, dateToPay: "15 July 2025" },
    products: [
      {
        name: "HP Pavilion 15 Laptop",
        productImage: "💻",
        soldUnitPrice: 120000,
        productCostPrice: 94000,
        quantity: 1,
        totalAmount: 120000,
        category: "Computers",
        returned: false,
      },
    ],
    comments: "Customer to clear balance before month end.",
  },
  {
    transID: "13062026_G5H6I",
    transactionDetails: {
      date: new Date("2025-06-14T14:15:00"),
      deliveryStatus: "notSupplied",
      paymentMode: "bankTransfer",
      numberOfItems: 17,
      subTotal: 32500,
      totalAmount: 34500,
      discount: 0,
      vat: 0,
      vatPercentage: 0,
      serviceCharge: 2000,
      deliveryFee: 0,
    },
    soldBy: { fullName: "Fatima Aliyu" },
    customer: { fullName: "Chukwuma Igwe", email: "", phoneNumber: "0812 345 6789", address: "Wuse 2, Abuja" },
    store: { id: "s3", name: "Pragma Superstore — Abuja" },
    operationType: { whichOperation: "cash" },
    products: [
      {
        name: "Indomie Noodles ×10",
        productImage: "🍜",
        soldUnitPrice: 1500,
        productCostPrice: 1100,
        quantity: 10,
        totalAmount: 15000,
        category: "Food",
        returned: false,
      },
      {
        name: "Peak Milk 400g ×5",
        productImage: "🥛",
        soldUnitPrice: 2500,
        productCostPrice: 1900,
        quantity: 5,
        totalAmount: 12500,
        category: "Dairy",
        returned: false,
      },
      {
        name: "Golden Sugar 1kg ×2",
        productImage: "🍚",
        soldUnitPrice: 2500,
        productCostPrice: 1800,
        quantity: 2,
        totalAmount: 5000,
        category: "Groceries",
        returned: false,
      },
    ],
    comments: "Bulk order — delivery Thursday morning.",
  },
  {
    transID: "13062026_J7K8L",
    transactionDetails: {
      date: new Date("2025-06-14T08:05:00"),
      deliveryStatus: "returned",
      paymentMode: "card",
      numberOfItems: 1,
      subTotal: 18000,
      totalAmount: 18000,
      discount: 0,
      vat: 0,
      vatPercentage: 0,
      serviceCharge: 0,
      deliveryFee: 0,
    },
    soldBy: { fullName: "Amaka Obi" },
    customer: { fullName: "Blessing Nwachukwu", email: "bnwachukwu@outlook.com", phoneNumber: "0701 112 2233", address: "" },
    store: { id: "s1", name: "Pragma Electronics — Ikeja" },
    operationType: { whichOperation: "cash" },
    products: [
      {
        name: "Sony Bluetooth Earphones",
        productImage: "🎧",
        soldUnitPrice: 18000,
        productCostPrice: 12000,
        quantity: 1,
        totalAmount: 18000,
        category: "Audio",
        returned: true,
      },
    ],
    comments: "Faulty unit returned. Refund processed.",
  },
  {
    transID: "12062026_M9N0O",
    transactionDetails: {
      date: new Date("2025-06-13T16:55:00"),
      deliveryStatus: "supplied",
      paymentMode: "cash",
      numberOfItems: 2,
      subTotal: 9500,
      totalAmount: 10175,
      discount: 0,
      vat: 675,
      vatPercentage: 7.5,
      serviceCharge: 0,
      deliveryFee: 0,
    },
    soldBy: { fullName: "Chisom Adaeze" },
    customer: { fullName: "Adaobi Nwosu", email: "", phoneNumber: "0809 988 7766", address: "" },
    store: { id: "s2", name: "StoreLense Hub — Lekki" },
    operationType: { whichOperation: "cash" },
    products: [
      {
        name: "4-Way Extension Cable",
        productImage: "🔌",
        soldUnitPrice: 5500,
        productCostPrice: 3200,
        quantity: 1,
        totalAmount: 5500,
        category: "Electronics",
        returned: false,
      },
      {
        name: "USB-C Hub 7-in-1",
        productImage: "🔗",
        soldUnitPrice: 4000,
        productCostPrice: 2500,
        quantity: 1,
        totalAmount: 4000,
        category: "Electronics",
        returned: false,
      },
    ],
    comments: null,
  },
  {
    transID: "12062026_P1Q2R",
    transactionDetails: {
      date: new Date("2025-06-13T10:30:00"),
      deliveryStatus: "supplied",
      paymentMode: "POS",
      numberOfItems: 4,
      subTotal: 58000,
      totalAmount: 58000,
      discount: 5000,
      vat: 0,
      vatPercentage: 0,
      serviceCharge: 0,
      deliveryFee: 0,
    },
    soldBy: { fullName: "Tunde Bello" },
    customer: { fullName: "Anonymous Customer", email: "", phoneNumber: "", address: "" },
    store: { id: "s1", name: "Pragma Electronics — Ikeja" },
    operationType: { whichOperation: "cash" },
    products: [
      {
        name: "Wireless Keyboard",
        productImage: "⌨️",
        soldUnitPrice: 22000,
        productCostPrice: 16000,
        quantity: 1,
        totalAmount: 22000,
        category: "Peripherals",
        returned: false,
      },
      {
        name: "Wireless Mouse",
        productImage: "🖱️",
        soldUnitPrice: 15000,
        productCostPrice: 10000,
        quantity: 1,
        totalAmount: 15000,
        category: "Peripherals",
        returned: false,
      },
    ],
    comments: null,
  },
];

/* ─────────────────── sub-components ─────────────────── */

// Product avatar circle
function ProdAvatar({ name = "?", size = 32, qty }) {
  const [bg, fg] = avatarColors(name);
  const label = qty > 1 ? qty : name.charAt(0).toUpperCase();
  return (
    <div className='tx-prod-avatar' style={{ width: size, height: size, background: bg, color: fg }}>
      {label}
    </div>
  );
}

// Badge
function Badge({ variant, children, dot }) {
  return (
    <span className={`tx-badge ${variant}`}>
      {dot && <span className='tx-badge-dot' />}
      {children}
    </span>
  );
}

function PaymentBadge({ mode }) {
  const map = {
    cash: { variant: "cash", icon: <Banknote size={11} />, label: "Cash" },
    card: { variant: "card", icon: <CreditCard size={11} />, label: "Card" },
    mobilePay: { variant: "mobile", icon: <Smartphone size={11} />, label: "Mobile Pay" },
    bankTransfer: { variant: "bank", icon: <Landmark size={11} />, label: "Bank Transfer" },
    POS: { variant: "pos", icon: <ScanLine size={11} />, label: "POS" },
  };
  const b = map[mode] || map.cash;
  return (
    <Badge variant={b.variant}>
      {b.icon}
      {b.label}
    </Badge>
  );
}

function DeliveryBadge({ status }) {
  const map = {
    supplied: { variant: "supplied", icon: <Check size={11} />, label: "Supplied" },
    pending: { variant: "pending", icon: <Clock size={11} />, label: "Pending" },
    notSupplied: { variant: "not-supplied", icon: <Package size={11} />, label: "Not Supplied" },
    returned: { variant: "returned", dot: true, label: "returned" },
  };
  const b = map[status] || map.pending;
  return (
    <Badge variant={b.variant} dot={b.dot}>
      {b.icon}
      {b.label}
    </Badge>
  );
}

// Toast
function Toast({ message }) {
  return <div className={`tx-toast ${message ? "visible" : ""}`}>{message}</div>;
}

/* ─────────────────── Filter overlay ─────────────────── */
function FilterOverlay({ onClose, filters, onChange }) {
  const [local, setLocal] = useState(filters);
  const pick = (key, val) => setLocal((p) => ({ ...p, [key]: val }));
  const reset = () => {
    const r = { delivery: "", payment: "", operation: "" };
    setLocal(r);
    onChange(r);
    onClose();
  };
  const apply = () => {
    onChange(local);
    onClose();
  };

  const Group = ({ label, optKey, options }) => (
    <div className='tx-filter-group'>
      <div className='tx-filter-group-label'>{label}</div>
      <div className='tx-filter-chips'>
        {options.map(([val, lbl]) => (
          <button key={val} className={`tx-filter-chip ${local[optKey] === val ? "active" : ""}`} onClick={() => pick(optKey, val)}>
            {lbl}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className='tx-filter-overlay' onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className='tx-filter-box'>
        <div className='tx-filter-header'>
          <h3>Filters</h3>
          <button className='tx-filter-close' onClick={onClose}>
            <X size={15} />
          </button>
        </div>
        <div className='tx-filter-body'>
          <Group
            label='Delivery status'
            optKey='delivery'
            options={[
              ["", "All"],
              ["supplied", "Supplied"],
              ["pending", "Pending"],
              ["notSupplied", "Not supplied"],
              ["returned", "Returned"],
            ]}
          />
          <Group
            label='Payment mode'
            optKey='payment'
            options={[
              ["", "All"],
              ["cash", "Cash"],
              ["card", "Card"],
              ["mobilePay", "Mobile Pay"],
              ["bankTransfer", "Bank Transfer"],
              ["POS", "POS"],
            ]}
          />
          <Group
            label='Operation type'
            optKey='operation'
            options={[
              ["", "Cash & Loan"],
              ["cash", "Cash only"],
              ["loan", "Loan only"],
            ]}
          />
        </div>
        <div className='tx-filter-footer'>
          <button className='tx-filter-reset' onClick={reset}>
            Reset
          </button>
          <button className='tx-filter-apply' onClick={apply}>
            Apply filters
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── Period overlay ─────────────────── */
function PeriodOverlay({ active, onSelect, onClose }) {
  return (
    <div className='tx-period-overlay' onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className='tx-period-box'>
        <div className='tx-period-header'>
          <h3>Select period</h3>
          <button className='tx-period-close' onClick={onClose}>
            <X size={14} />
          </button>
        </div>
        <div>
          {PERIODS.map((p) => (
            <button
              key={p}
              className={`tx-period-option ${active === p ? "active" : ""}`}
              onClick={() => {
                onSelect(p);
                onClose();
              }}
            >
              {p}
              <span className='tx-period-check' />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── Insights overlay ─────────────────── */
function InsightsOverlay({ txList, period, onClose, currency = "#" }) {
  const stats = useMemo(() => {
    const tot = txList.reduce((s, t) => s + t.transactionDetails.totalAmount, 0);
    const vat = txList.reduce((s, t) => s + t.transactionDetails.vat, 0);
    const loans = txList.filter((t) => t.operationType.whichOperation === "loan");
    const sup = txList.filter((t) => t.transactionDetails.deliveryStatus === "supplied").length;
    const profit = txList.reduce((s, t) => s + t.products.reduce((ps, p) => ps + (p.soldUnitPrice - p.productCostPrice) * p.quantity, 0), 0);
    const pend = txList.filter((t) => ["pending", "notSupplied"].includes(t.transactionDetails.deliveryStatus)).length;
    const ret = txList.filter((t) => t.transactionDetails.deliveryStatus === "returned").length;
    const lbal = loans.reduce((s, t) => s + (t.operationType.balance || 0), 0);
    return { tot, vat, loans: loans.length, sup, profit, pend, ret, lbal, avg: txList.length ? tot / txList.length : 0 };
  }, [txList]);

  return (
    <div className='tx-insights-overlay' onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className='tx-insights-sheet'>
        <div className='tx-sheet-handle' />
        <div className='tx-insights-header'>
          <div>
            <h3>Insights</h3>
            <div className='tx-insights-sub'>
              {period} · {txList.length} transactions
            </div>
          </div>
          <button className='tx-insights-close' onClick={onClose}>
            <X size={15} />
          </button>
        </div>
        <div className='tx-insights-body'>
          <div className='tx-stat-grid'>
            {[
              {
                cls: "vol",
                label: "Volume",
                val: fmtC(stats.tot, currency),
                meta: `${txList.length} transactions`,
                delta: "up",
                deltaText: "14.2% vs last period",
                Icon: BarChart2,
              },
              {
                cls: "vat",
                label: "VAT Collected",
                val: fmtC(stats.vat, currency),
                meta: `${txList.length} transactions`,
                delta: "up",
                deltaText: "7.5% rate applied",
                Icon: CreditCard,
              },
              {
                cls: "loan",
                label: "Active Loans",
                val: stats.loans,
                meta: `${txList.length} transactions`,
                delta: "dn",
                deltaText: "Outstanding balance",
                Icon: Wallet,
              },
              {
                cls: "sup",
                label: "Supplied",
                val: stats.sup,
                meta: `${txList.length} transactions`,
                delta: "up",
                deltaText: "Supply rate",
                Icon: PackageCheck,
              },
            ].map((s) => (
              <div key={s.cls} className={`tx-stat-card ${s.cls}`}>
                <div className='tx-stat-label'>
                  <s.Icon size={13} />
                  {s.label}
                </div>
                <div className='tx-stat-value'>{s.val}</div>
                <div className='tx-stat-meta'>{s.meta}</div>
                <div className={`tx-stat-delta ${s.delta}`}>
                  {s.delta === "up" ? <TrendingUp size={10} /> : <TrendingUp size={10} style={{ transform: "rotate(180deg)" }} />}
                  {s.deltaText}
                </div>
              </div>
            ))}
          </div>
          <div className='tx-insights-divider' />
          <div className='tx-insights-breakdown'>
            {[
              ["Total profit", fmtC(stats.profit, currency)],
              ["Avg transaction value", fmtC(Math.round(stats.avg), currency)],
              ["Pending / not supplied", stats.pend],
              ["Returned items", stats.ret],
              ["Outstanding loan balance", fmtC(stats.lbal, currency), "#a82318"],
            ].map(([label, value, color]) => (
              <div key={label} className='tx-breakdown-row'>
                <span className='label'>{label}</span>
                <span className='value' style={color ? { color } : {}}>
                  {value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── Detail sheet ─────────────────── */
/* function DetailSheet({ tx, updateSelectedTx, onClose, onMarkSupplied, onReturnItem, currency, setTxData }) {
  const [showReceipt, setShowReceipt] = useState(false);
  const [openClearLoanModal, setOpenClearLoanModal] = useState(false);
  const [openExtendLoanModal, setOpenExtendLoanModal] = useState(false);
  const [openSplitPaymentModal, setOpenSplitPaymentModal] = useState(false);
  const [newExtendDate, setNewExtendDate] = useState("");
  const [isExtendDateValid, setIsExtendDateValid] = useState(false);
  const [splitPaymentAmount, setSplitPaymentAmount] = useState(null);
  const [loanPaymentMode, setLoanPaymentMode] = useState("cash"); // ["cash", "card", "mobilePay"]
  const [isSplitPaymentAmountValid, setIsSplitPaymentAmountValid] = useState(false);
  const [isReturningAll, setIsReturningAll] = useState(false);
  const [isSupplying, setIsSupplying] = useState(false);
  const [isReturningOne, setIsReturningOne] = useState(null);

  const navigate = useNavigate();
  if (!tx) return null;
  const d = tx.transactionDetails;
  const isL = tx.operationType.whichOperation === "loan";
  const isCleared = isL && (tx.operationType.balance || 0) <= 0;
  const s = tx.store.id || {};
 

  const allReturned = tx.products.every((p) => p.returned);
  const status = d.deliveryStatus;

  const pct = isL && d.totalAmount ? Math.round((((tx.operationType.intialPayment || 0) + (tx.operationType.clearedAmount || 0)) / d.totalAmount) * 100) : 0;

  const isOverdue = isL && tx.operationType.dateToPay && new Date(tx.operationType.dateToPay) < new Date() && (tx.operationType.balance || 0) > 0;

 
  const ActionIcon = ({ variant, icon: Icon, label, onClick }) => (
    <button className={`tx-icon-action tx-ia-${variant}`} onClick={onClick}>
      <div className='tx-icon-action-icon'>{label === "returning..." || label === "supplying..." ? <ButtonLoaderII size={22} /> : <Icon size={22} />}</div>
      <span className='tx-icon-action-label'>{label}</span>
    </button>
  );

  const actions = (() => {
    if (status === "returned" || allReturned) return [{ variant: "ghost", icon: Store, label: "View store", fn: () => {} }];
    if (isL)
      return [
        {
          variant: isCleared ? "disabled" : "navy",
          icon: Split,
          label: "Split pay",
          fn: () => {
            if (isCleared) {
              uiAlert(`Loan is fully cleared. No further payments needed.`, "info");
              return;
            }

            //open splitpayment panel

            setOpenSplitPaymentModal(true);

           
          },
        },
        {
          variant: isCleared ? "disabled" : "green",
          icon: CheckCircle,
          label: "Clear credit",
          fn: () => {
            if (isCleared) {
              uiAlert(`Loan is fully cleared. No further payments needed.`, "info");
              return;
            }
            setOpenClearLoanModal(true);
          
          },
        },
        {
          variant: isCleared ? "disabled" : "amber",
          icon: ClockArrowUp,
          label: "Extend",
          fn: () => {
            if (isCleared) {
              uiAlert(`Loan is fully cleared. No further payments needed.`, "info");
              return;
            }
            setOpenExtendLoanModal(true);
          
          },
        },
        {
          variant: isReturningAll ? "disabled" : "red",
          icon: Undo2,
          label: isReturningAll ? "returning..." : "Return all",
          fn: () => {
        
            if (isReturningAll) return;

            if (window.confirm("Are you sure you want to return all items?\nAll items will be added back to your stock. This action can't be undone."))
              return returnAllItems();
          },
        },
        {
          variant: "ghost",
          icon: Printer,
          label: "Print",
          fn: () => {
            return handlePrintReceipt();
          },
        },
        {
          variant: "ghost",
          icon: Store,
          label: "View store",
          fn: () => {},
        },
      ];
    if (status === "notSupplied" || status === "pending")
      return [
        {
          variant: isSupplying ? "disabled" : "blue",
          icon: PackageCheck,
          label: isSupplying ? "supplying..." : "Mark supplied",
          fn: async () => {
            try {
              if (isSupplying) return;
              setIsSupplying(true);
              await onMarkSupplied(tx.transID);
            } catch (e) {
            } finally {
              setIsSupplying(false);
            }
          },
        },
        {
          variant: isReturningAll ? "disabled" : "red",
          icon: Undo2,
          label: isReturningAll ? "returning..." : "Return all",
          fn: () => {
            if (isReturningAll) return;
            if (window.confirm("Are you sure you want to return all items?\nAll items will be added back to your stock. This action can't be undone."))
              return returnAllItems();
          },
        },
        {
          variant: "ghost",
          icon: Printer,
          label: "Print",
          fn: () => {
            return handlePrintReceipt();
          },
        },
        {
          variant: "ghost",
          icon: Store,
          label: "View store",
          fn: () => {},
        },
      ];
    return [
      {
        variant: isReturningAll ? "disabled" : "red",
        icon: Undo2,
        label: isReturningAll ? "returning..." : "Return all",
        fn: () => {
          if (isReturningAll) return;
          if (window.confirm("Are you sure you want to return all items?\nAll items will be added back to your stock. This action can't be undone."))
            return returnAllItems();
        },
      },
      {
        variant: "ghost",
        icon: Printer,
        label: "Print",
        fn: () => {
          return handlePrintReceipt();
        },
      },
      {
        variant: "ghost",
        icon: Store,
        label: "View store",
        fn: () => {},
      },
    ];
  })();

  const handlePrintReceipt = () => {
    setShowReceipt(true);
    setTimeout(() => {
      setShowReceipt(false);
    }, 5000);
  };

  const returnAllItems = async () => {
    try {
      setIsReturningAll(true);
      const url = returnAllProductUrl + tx.store.id._id;
      let payload = {
        transactionID: tx.transID,
        productIDs: tx.products.map((product) => product.id),
        customer: tx.customer.fullName,
      };

      let response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to return all items.");
      }

      let { transaction } = await response.json();

      updateSelectedTx(transaction);

      setTxData((prevData) => {
        const updatedData = prevData.map((txItem) => {
          if (txItem.transID === transaction.transID) {
            return transaction;
          }
          return txItem;
        });
        return updatedData;
      });

      uiAlert("All items have been returned to your stock", "success");
    } catch (error) {
      uiAlert(error.message || "Failed to return all items. Please try again", "error");
    } finally {
      setIsReturningAll(false);
    }
  };

  const clearLoan = async () => {
    let payload = { transactionID: tx.transID, totalClearedAmount: tx.operationType.balance, paymentMode: loanPaymentMode };

    if (tx.transactionDetails.deliveryStatus === "returned") return uiAlert("This loan has been returned. No need to clear the loan.", "info");
    const url = clearLoanUrl + tx.store.id._id;

    console.log(url);

    try {
      let response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to clear loan.");
      }

      const { transaction, clearedLoanToday } = await response.json();
      updateSelectedTx(transaction);

      setTxData((prevData) => {
        const updatedData = prevData.map((txItem) => {
          if (txItem.transID === transaction.transID) {
            return transaction;
          }
          return txItem;
        });
        return updatedData;
      });

      uiAlert(`Loan cleared successfully.`, "success");
      return true;
    } catch (error) {
      uiAlert(error.message || "Failed to clear loan.", "error");
      return false;
    }
  };

  const extendLoanFun = async () => {
    if (tx.transactionDetails.deliveryStatus === "returned") return uiAlert("This loan has been returned. No need to extend the due date.", "info");
    setIsExtendDateValid(false);
    const payload = { transactionID: tx.transID, newDate: newExtendDate };
    const url = extendLoanUrl + tx.store.id._id;

    try {
      let response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to extend loan.");
      }

      const { transaction } = await response.json();
      updateSelectedTx(transaction);

      setTxData((prevData) => {
        const updatedData = prevData.map((txItem) => {
          if (txItem.transID === transaction.transID) {
            return transaction;
          }
          return txItem;
        });
        return updatedData;
      });

      uiAlert(`Loan extended successfully.`, "success");
      return true;
    } catch (error) {
      uiAlert(error.message || "Failed to extend loan.", "error");
      return false;
    }
  };

  const splitPaymentFun = async () => {
    if (tx.transactionDetails.deliveryStatus === "returned") return uiAlert("This loan has been returned. No need to make a payment.", "info");
    setIsSplitPaymentAmountValid(false);
    const payload = { transactionID: tx.transID, amountCleared: splitPaymentAmount, paymentMode: loanPaymentMode };
    const url = splitPaymentUrl + tx.store.id._id;

    try {
      let response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to make split payment.");
      }

      const { transaction } = await response.json();
      updateSelectedTx(transaction);

      setTxData((prevData) => {
        const updatedData = prevData.map((txItem) => {
          if (txItem.transID === transaction.transID) {
            return transaction;
          }
          return txItem;
        });
        return updatedData;
      });

      uiAlert(`Split payment made successfully.`, "success");
      return true;
    } catch (error) {
      uiAlert(error.message || "Failed to make split payment.", "error");
      return false;
    }
  };

  return (
    <>
      <div className='tx-overlay' onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className='tx-sheet'>
          <div className='tx-sheet-handle' />

         
          <div className='tx-sheet-header'>
            <div className='tx-sheet-header-top'>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className='tx-sheet-store-label'>
                  <Store size={13} />
                  {tx.store.name}
                </div>
                <div className='tx-sheet-amount'>{fmt(d.totalAmount, currency)}</div>
                <div className='tx-sheet-date'>
                  {d.date
                    ? new Date(d.date).toLocaleDateString("en-NG", {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })
                    : ""}
                </div>
              </div>
              <button className='tx-sheet-close' onClick={onClose}>
                <X size={16} />
              </button>
            </div>
            <div className='tx-sheet-badges'>
              <PaymentBadge mode={d.paymentMode} />
              <DeliveryBadge status={d.deliveryStatus} />
              {isL && (
                <Badge variant='loan' dot>
                  Loan
                </Badge>
              )}
              {isOverdue && (
                <Badge variant='overdue' dot>
                  {Math.floor((new Date() - new Date(tx.operationType.dateToPay)) / 86400000)}d overdue
                </Badge>
              )}
            </div>
          
            <div className='tx-icon-actions'>
              {actions.map((a, i) => (
                <ActionIcon
                  key={i}
                  variant={a.variant}
                  icon={a.icon}
                  label={a.label}
                  onClick={() => {
                    if (a.label === "View store") {
                      navigate(`/mystores/ ${tx.store.id.storeName} /${tx.store.id._id}`);

                      onClose();
                      return;
                    }

                    a.fn();
                  }}
                />
              ))}
            </div>
          </div>

         
          <div className='tx-sheet-section'>
            <div className='tx-section-title'>
              <Store size={14} />
              Store details
            </div>
            <div className='tx-store-card'>
              <div className='tx-store-card-name'>{s.storeName || tx.store.name}</div>
              <div className='tx-store-card-meta'>
                {s.category && (
                  <span>
                    <Package size={11} />
                    {s.category}
                  </span>
                )}
                {s.storeAddress && (
                  <span>
                    <MapPin size={11} />
                    {s.storeAddress}
                  </span>
                )}
                {s.isStoreVerified ? (
                  <span style={{ color: "#2563eb" }}>
                    <BadgeCheck size={11} />
                    Verified
                  </span>
                ) : null}
                {
                  <span>
                    <Star size={11} />
                    {s.averageRating ? s.averageRating : "no rating"}
                  </span>
                }
              </div>
              <div className='tx-store-card-grid'>
                {[
                  ["Mode", s.superMarketMode ? "Supermarket" : "Retail"],

                  ["VAT rate", s.isVATenabled ? `${s.vatPercentage?.$numberDecimal}%` : "Disabled"],
                  ["Target sales", s.targetSales ? fmtC(s.targetSales, currency) : "—"],
                  ["Currency", s.currency || "NGN"],
                ].map(([l, v]) => (
                  <div key={l} className='tx-store-card-cell'>
                    <div className='tx-store-card-cell-label'>{l}</div>
                    <div className='tx-store-card-cell-val'>{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

       
          {isL && (
            <div className='tx-sheet-section'>
              <div className='tx-section-title'>
                <Wallet size={14} />
                Loan overview
              </div>
              <DetailRow label='Initial payment' value={fmt(tx.operationType.intialPayment || 0, currency)} />
              <DetailRow label='Cleared amount' value={fmt(tx.operationType.clearedAmount || 0, currency)} valueStyle={{ color: "rgb(12, 134, 49)" }} />
              <DetailRow label='Balance remaining' value={fmt(tx.operationType.balance || 0, currency)} valueStyle={{ color: "#a82318" }} />
              <DetailRow label='Due date' value={tx.operationType.dateToPay || "—"} />
              <div className='tx-loan-progress-track'>
                <div className='tx-loan-progress-fill' style={{ width: `${pct}%` }} />
              </div>
              <div className='tx-loan-progress-labels'>
                <span>{pct}% paid</span>
                <span>{fmt(tx.operationType.balance || 0, currency)} remaining</span>
              </div>
            </div>
          )}

         
          <div className='tx-sheet-section'>
            <div className='tx-section-title'>
              <Banknote size={14} />
              Transaction
            </div>
            <DetailRow label='Trans ID' value={truncateID(tx.transID)} mono />
            <DetailRow
              label='Date & time'
              value={new Date(d.date).toLocaleString("en-NG", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
            />
            <DetailRow
              label='Payment'
              value={{ cash: "Cash", card: "Card", mobilePay: "Mobile Pay", bankTransfer: "Bank Transfer", POS: "POS" }[d.paymentMode] || d.paymentMode}
            />
            <DetailRow label='Operation' value={isL ? "Loan / Credit sale" : "Cash sale"} />
            <DetailRow label='Sold by' value={tx.soldBy.fullName || "—"} />
            <DetailRow label='Items' value={`${tx.products.length} lines · ${d.numberOfItems} units`} />
          </div>

          
          <div className='tx-sheet-section'>
            <div className='tx-section-title'>
              <User size={14} />
              Customer
            </div>
            <DetailRow label='Name' value={tx.customer.fullName || "Anonymous Customer"} />
            <DetailRow label='Phone' value={tx.customer.phoneNumber || "Not provided"} />
            {tx.customer.email && <DetailRow label='Email' value={tx.customer.email} />}
            {tx.customer.address && <DetailRow label='Address' value={tx.customer.address} />}
          </div>

          <div className='tx-sheet-section'>
            <div className='tx-section-title'>
              <Package size={14} />
              Items purchased
            </div>
            {tx.products.map((p, i) => {
              const profit = (p.soldUnitPrice - p.productCostPrice) * p.quantity;
              return (
                <div key={i} className='tx-product-row'>
                  <div className='tx-product-icon'>
                    {p.productImage ? (
                      <img src={p.productImage} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <ProdAvatar name={p.name} qty={p.quantity} />
                    )}
                  </div>
                  <div className='tx-product-info'>
                    <div className='tx-product-name'>{p.name}</div>
                    <div className='tx-product-sub'>
                      {p.category} · ×{p.quantity} {p.unit} @ {fmt(p.soldUnitPrice, currency)}
                    </div>
                    <div className='tx-product-profit'>
                      <TrendingUp size={11} />
                      Profit {fmt(profit, currency)}
                    </div>
                    {isL ? null : (
                      <div className='tx-product-actions'>
                        {p.returned ? (
                          <span style={{ backgroundColor: "rgb(215, 215, 215)", color: "rgb(92, 92, 92)", border: "none" }} className='tx-returned-tag'>
                            Item Returned
                          </span>
                        ) : (
                          <button
                            className='tx-return-btn'
                            onClick={async () => {
                              try {
                                setIsReturningOne(i);
                                await onReturnItem(tx.transID, i);
                              } catch (e) {
                              } finally {
                                setIsReturningOne(null);
                              }
                            }}
                            disabled={isReturningOne === i}
                          >
                            {isReturningOne === i ? (
                              <ButtonLoaderII size={12} color='gray' />
                            ) : (
                              <>
                                <CornerUpLeft size={11} />
                                Return item
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className='tx-product-amount'>
                    <div className='total'>{fmt(p.totalAmount, currency)}</div>
                    <div className='sub'>subtotal</div>
                  </div>
                </div>
              );
            })}
          </div>
    

     
          <div className='tx-sheet-section'>
            <div className='tx-section-title'>
              <BarChart2 size={14} />
              Breakdown
            </div>
            <DetailRow label='Subtotal' value={fmt(d.subTotal, currency)} />

            {d.discount > 0 && <DetailRow label='Discount' value={`−${fmt(d.discount, currency)}`} valueStyle={{ color: "#156b43" }} />}

            {d.vat > 0 && <DetailRow label={`VAT (${d.vatPercentage?.$numberDecimal}%)`} value={fmt(d.vat, currency)} />}

            {d.serviceCharge > 0 && <DetailRow label='Service charge' value={fmt(d.serviceCharge, currency)} />}

            {d.deliveryFee > 0 && <DetailRow label='Delivery fee' value={fmt(d.deliveryFee, currency)} />}

            {d.clearedbalance > 0 && <DetailRow label='Extra payment (Off record debt cleared)' value={fmt(d.clearedbalance, currency)} />}

            {d.extraPayment > 0 && <DetailRow label='Extra payment' value={fmt(d.extraPayment, currency)} />}

            <div className='tx-breakdown-total'>
              <span className='label'>Total</span>
              <span className='value'>{fmt(d.totalAmount, currency)}</span>
            </div>
          </div>

         
          {tx.comments && (
            <div className='tx-sheet-section'>
              <div className='tx-section-title'>
                <BadgeCheck size={14} />
                Comment
              </div>
              <p style={{ fontSize: 13, color: "#4a6580", lineHeight: 1.6 }}>{tx.comments}</p>
            </div>
          )}
        </div>
      </div>

      {openClearLoanModal && (
        <ClearLoanModal
          isOpen={openClearLoanModal}
          amount={tx.operationType?.balance}
          onCancel={() => {
            setOpenClearLoanModal(false);
          }}
          onConfirm={async () => {
            return await clearLoan();
          }}
          loanPaymentMode={loanPaymentMode}
          setLoanPaymentMode={setLoanPaymentMode}
        />
      )}

      {openExtendLoanModal && (
        <ExtendLoanModal
          isOpen={openExtendLoanModal}
          dueDate={tx.operationType.dateToPay}
          onCancel={() => {
            setOpenExtendLoanModal(false);
          }}
          onConfirm={async () => {
            return await extendLoanFun();
          }}
          newExtendDate={newExtendDate}
          setNewExtendDate={setNewExtendDate}
        />
      )}

      {openSplitPaymentModal && (
        <RegisterPaymentModal
          isOpen={openSplitPaymentModal}
          balance={tx.operationType?.balance}
          onCancel={() => {
            setOpenSplitPaymentModal(false);
          }}
          onConfirm={async () => {
            return await splitPaymentFun();
          }}
          splitPaymentAmount={splitPaymentAmount}
          setSplitPaymentAmount={setSplitPaymentAmount}
          loanPaymentMode={loanPaymentMode}
          setLoanPaymentMode={setLoanPaymentMode}
          currency={currency}
        />
      )}

      {showReceipt && <InvoicePDF invoiceData={tx} />}
    </>
  );
} */

/* function DetailRow({ label, value, mono, valueStyle }) {
  return (
    <div className='tx-detail-row'>
      <span className='tx-dl'>{label}</span>
      <span className={`tx-dv${mono ? " mono" : ""}`} style={valueStyle}>
        {value}
      </span>
    </div>
  );
} */

// paymentType -> { label, color, bg }. Mirrors the receivingAccount /
// transactionDetails.payments schema enum exactly:
// ["cash", "POS", "bankTransfer", "mobilePay", "card", "storelensePOS"]
// "split" is included too since transactionDetails.paymentMode can be
// "split" as the transaction's overall mode.
// paymentType -> { label, color, bg }. Mirrors the receivingAccount /
// transactionDetails.payments schema enum exactly:
// ["cash", "POS", "bankTransfer", "mobilePay", "card", "storelensePOS"]
// "split" is included too since transactionDetails.paymentMode can be
// "split" as the transaction's overall mode.
const PAYMENT_TYPE_META = {
  cash: { label: "Cash", color: "#16a34a", bg: "#dcfce7" },
  POS: { label: "POS", color: "#4f46e5", bg: "#e0e7ff" },
  bankTransfer: { label: "Bank Transfer", color: "#ea580c", bg: "#ffedd5" },
  mobilePay: { label: "Mobile Pay", color: "#0d9488", bg: "#ccfbf1" },
  card: { label: "Card", color: "#0369a1", bg: "#f0f9ff" },
  storelensePOS: { label: "StoreLense POS", color: "#475569", bg: "#f1f5f9" },
  split: { label: "Split", color: "#c026d3", bg: "#fae8ff" },
};

function PaymentTypeTag({ type }) {
  const meta = PAYMENT_TYPE_META[type] || PAYMENT_TYPE_META.cash;
  return (
    <span className='tx-payment-type-tag' style={{ color: meta.color, background: meta.bg }}>
      {meta.label}
    </span>
  );
}

function DetailSheet({ tx, updateSelectedTx, onClose, onMarkSupplied, onReturnItem, currency, setTxData, userSocket }) {
  const [showReceipt, setShowReceipt] = useState(false);
  const [openClearLoanModal, setOpenClearLoanModal] = useState(false);
  const [openExtendLoanModal, setOpenExtendLoanModal] = useState(false);
  const [openSplitPaymentModal, setOpenSplitPaymentModal] = useState(false);
  const [newExtendDate, setNewExtendDate] = useState("");
  const [isExtendDateValid, setIsExtendDateValid] = useState(false);
  const [splitPaymentAmount, setSplitPaymentAmount] = useState(null);
  const [loanPaymentMode, setLoanPaymentMode] = useState("cash"); // ["cash", "card", "mobilePay"]
  const [isSplitPaymentAmountValid, setIsSplitPaymentAmountValid] = useState(false);
  const [isReturningAll, setIsReturningAll] = useState(false);
  const [isSupplying, setIsSupplying] = useState(false);
  const [isReturningOne, setIsReturningOne] = useState(null);

  const navigate = useNavigate();
  if (!tx) return null;
  const d = tx.transactionDetails;
  const isL = tx.operationType.whichOperation === "loan";
  const isCleared = isL && (tx.operationType.balance || 0) <= 0;
  const s = tx.store.id || {};
  /* const s = STORES[tx.store.id] || {}; */

  const allReturned = tx.products.every((p) => p.returned);
  const status = d.deliveryStatus;

  const pct = isL && d.totalAmount ? Math.round((((tx.operationType.intialPayment || 0) + (tx.operationType.clearedAmount || 0)) / d.totalAmount) * 100) : 0;

  const isOverdue = isL && tx.operationType.dateToPay && new Date(tx.operationType.dateToPay) < new Date() && (tx.operationType.balance || 0) > 0;

  /* Icon action bar */
  const ActionIcon = ({ variant, icon: Icon, label, onClick }) => (
    <button className={`tx-icon-action tx-ia-${variant}`} onClick={onClick}>
      <div className='tx-icon-action-icon'>{label === "returning..." || label === "supplying..." ? <ButtonLoaderII size={22} /> : <Icon size={22} />}</div>
      <span className='tx-icon-action-label'>{label}</span>
    </button>
  );

  const actions = (() => {
    if (status === "returned" || allReturned) return [{ variant: "ghost", icon: Store, label: "View store", fn: () => {} }];
    if (isL)
      return [
        {
          variant: isCleared ? "disabled" : "navy",
          icon: Split,
          label: "Part pay",
          fn: () => {
            if (isCleared) {
              uiAlert(`Loan is fully cleared. No further payments needed.`, "info");
              return;
            }

            //open splitpayment panel

            setOpenSplitPaymentModal(true);

            /*   uiAlert(`Login into the store to perform this action.`, "info");
            return; */
          },
        },
        {
          variant: isCleared ? "disabled" : "green",
          icon: CheckCircle,
          label: "Clear credit",
          fn: () => {
            if (isCleared) {
              uiAlert(`Loan is fully cleared. No further payments needed.`, "info");
              return;
            }
            setOpenClearLoanModal(true);
            /*  uiAlert(`Login into the store to perform this action.`, "info");
            return; */
          },
        },
        {
          variant: isCleared ? "disabled" : "amber",
          icon: ClockArrowUp,
          label: "Extend",
          fn: () => {
            if (isCleared) {
              uiAlert(`Loan is fully cleared. No further payments needed.`, "info");
              return;
            }
            setOpenExtendLoanModal(true);
            /* uiAlert(`Login into the store to perform this action.`, "info");
            return; */
          },
        },
        {
          variant: isReturningAll ? "disabled" : "red",
          icon: Undo2,
          label: isReturningAll ? "returning..." : "Return all",
          fn: () => {
            /* uiAlert(`Login into the store to perform this action.`, "info"); */
            if (isReturningAll) return;

            if (window.confirm("Are you sure you want to return all items?\nAll items will be added back to your stock. This action can't be undone."))
              return returnAllItems();
          },
        },
        {
          variant: "ghost",
          icon: Printer,
          label: "Print",
          fn: () => {
            return handlePrintReceipt();
          },
        },
        {
          variant: "ghost",
          icon: Store,
          label: "View store",
          fn: () => {},
        },
      ];
    if (status === "notSupplied" || status === "pending")
      return [
        {
          variant: isSupplying ? "disabled" : "blue",
          icon: PackageCheck,
          label: isSupplying ? "supplying..." : "Mark supplied",
          fn: async () => {
            try {
              if (isSupplying) return;
              setIsSupplying(true);
              await onMarkSupplied(tx.transID);
            } catch (e) {
            } finally {
              setIsSupplying(false);
            }
          },
        },
        {
          variant: isReturningAll ? "disabled" : "red",
          icon: Undo2,
          label: isReturningAll ? "returning..." : "Return all",
          fn: () => {
            if (isReturningAll) return;
            if (window.confirm("Are you sure you want to return all items?\nAll items will be added back to your stock. This action can't be undone."))
              return returnAllItems();
          },
        },
        {
          variant: "ghost",
          icon: Printer,
          label: "Print",
          fn: () => {
            return handlePrintReceipt();
          },
        },
        {
          variant: "ghost",
          icon: Store,
          label: "View store",
          fn: () => {},
        },
      ];
    return [
      {
        variant: isReturningAll ? "disabled" : "red",
        icon: Undo2,
        label: isReturningAll ? "returning..." : "Return all",
        fn: () => {
          if (isReturningAll) return;
          if (window.confirm("Are you sure you want to return all items?\nAll items will be added back to your stock. This action can't be undone."))
            return returnAllItems();
        },
      },
      {
        variant: "ghost",
        icon: Printer,
        label: "Print",
        fn: () => {
          return handlePrintReceipt();
        },
      },
      {
        variant: "ghost",
        icon: Store,
        label: "View store",
        fn: () => {},
      },
    ];
  })();

  const handlePrintReceipt = () => {
    setShowReceipt(true);
    setTimeout(() => {
      setShowReceipt(false);
    }, 5000);
  };

  const returnAllItems = async () => {
    try {
      setIsReturningAll(true);
      const url = returnAllProductUrl + tx.store.id._id;
      let payload = {
        transactionID: tx.transID,
        productIDs: tx.products.map((product) => product.id),
        customer: tx.customer.fullName,
      };

      let response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to return all items.");
      }

      let { transaction } = await response.json();

      updateSelectedTx(transaction);

      setTxData((prevData) => {
        const updatedData = prevData.map((txItem) => {
          if (txItem.transID === transaction.transID) {
            return transaction;
          }
          return txItem;
        });
        return updatedData;
      });

      uiAlert("All items have been returned to your stock", "success");
    } catch (error) {
      uiAlert(error.message || "Failed to return all items. Please try again", "error");
    } finally {
      setIsReturningAll(false);
    }
  };

  const clearLoan = async (payments, paymentMode, amount) => {
    let payload = { transactionID: tx.transID, totalClearedAmount: tx.operationType.balance, paymentMode, payments, amount };

    if (tx.transactionDetails.deliveryStatus === "returned") return uiAlert("This loan has been returned. No need to clear the loan.", "info");
    const url = clearLoanUrl + tx.store.id._id;

    console.log(url);

    try {
      let response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to clear loan.");
      }

      const { transaction, clearedLoanToday } = await response.json();
      updateSelectedTx(transaction);

      setTxData((prevData) => {
        const updatedData = prevData.map((txItem) => {
          if (txItem.transID === transaction.transID) {
            return transaction;
          }
          return txItem;
        });
        return updatedData;
      });

      uiAlert(`Loan cleared successfully.`, "success");
      return true;
    } catch (error) {
      uiAlert(error.message || "Failed to clear loan.", "error");
      return false;
    }
  };

  const extendLoanFun = async () => {
    if (tx.transactionDetails.deliveryStatus === "returned") return uiAlert("This loan has been returned. No need to extend the due date.", "info");
    setIsExtendDateValid(false);
    const payload = { transactionID: tx.transID, newDate: newExtendDate };
    const url = extendLoanUrl + tx.store.id._id;

    try {
      let response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to extend loan.");
      }

      const { transaction } = await response.json();
      updateSelectedTx(transaction);

      setTxData((prevData) => {
        const updatedData = prevData.map((txItem) => {
          if (txItem.transID === transaction.transID) {
            return transaction;
          }
          return txItem;
        });
        return updatedData;
      });

      uiAlert(`Loan extended successfully.`, "success");
      return true;
    } catch (error) {
      uiAlert(error.message || "Failed to extend loan.", "error");
      return false;
    }
  };

  const splitPaymentFun = async (splitPaymentAmount, loanPaymentMode, payment) => {
    if (tx.transactionDetails.deliveryStatus === "returned") return uiAlert("This loan has been returned. No need to make a payment.", "info");
    setIsSplitPaymentAmountValid(false);

    const payload = {
      transactionID: tx.transID,
      amountCleared: splitPaymentAmount,
      paymentMode: loanPaymentMode,
      payments: payment,
      amount: splitPaymentAmount,
    };
    const url = splitPaymentUrl + tx.store.id._id;

    console.log(url, payload);

    try {
      let response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to make Part payment.");
      }

      const { transaction } = await response.json();
      updateSelectedTx(transaction);

      setTxData((prevData) => {
        const updatedData = prevData.map((txItem) => {
          if (txItem.transID === transaction.transID) {
            return transaction;
          }
          return txItem;
        });
        return updatedData;
      });

      uiAlert(`Part payment made successfully.`, "success");
      return true;
    } catch (error) {
      uiAlert(error.message || "Failed to make Part payment.", "error");
      return false;
    }
  };

  const pushToTerminal = async ({ accountId, accountName, provider, amount, sellToken, postype, serialNumber, transID }) => {
    // try to push to KUDA terminal

    console.log(accountId, accountName, provider, amount, sellToken, postype, serialNumber, transID);

    const url = `${pushPaymentKudaUrl}${tx.store.id._id}/`;

    try {
      let response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify({ accountId, accountName, provider, amount, sellToken, postype, serialNumber, transID: transID || tx.transID }),
      });

      /* uiAlert(`Successfully pushed ${amount} to ${accountName} via ${provider}`, "success"); */

      /*    return { success: true, message: "Transaction successful" }; */
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        return {
          success: false,
          message: data?.message || "Failed to push to terminal",
          data,
        };
      }

      return {
        success: true,
        message: data?.message || (response.ok ? "Transaction successful" : "Failed to push to terminal"),
        data,
      };
    } catch (error) {
      console.error("Error pushing to terminal:", error);
      return {
        success: false,
        message: error.message || "Failed to push to terminal",
        error,
      };
    }

    /* return uiAlert(`Pushing ${user.userCountryCurrencySymbol}${amount} to ${accountName} via ${provider} is pending `, "info"); */

    /* return new Promise((resolve, reject) => {
       
         // Simulate a successful push to terminal
         const success = true; // Change this to false to simulate failure
         if (success) {
           resolve();
         } else {
           uiAlert(`Failed to push ${amount} to ${accountName} via ${provider}`, "error");
           reject(new Error("Failed to push to terminal"));
         }
      
     }) */
  };

  return (
    <>
      <div className='tx-overlay' onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className='tx-sheet'>
          <div className='tx-sheet-handle' />

          {/* Sticky header */}
          <div className='tx-sheet-header'>
            <div className='tx-sheet-header-top'>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className='tx-sheet-store-label'>
                  <Store size={13} />
                  {tx.store.name}
                </div>
                <div className='tx-sheet-amount'>{fmt(d.totalAmount, currency)}</div>
                <div className='tx-sheet-date'>
                  {d.date
                    ? new Date(d.date).toLocaleDateString("en-NG", {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })
                    : ""}
                </div>
              </div>
              <button className='tx-sheet-close' onClick={onClose}>
                <X size={16} />
              </button>
            </div>
            <div className='tx-sheet-badges'>
              <PaymentBadge mode={d.paymentMode} />
              <DeliveryBadge status={d.deliveryStatus} />
              {isL && (
                <Badge variant='loan' dot>
                  Loan
                </Badge>
              )}
              {isOverdue && (
                <Badge variant='overdue' dot>
                  {Math.floor((new Date() - new Date(tx.operationType.dateToPay)) / 86400000)}d overdue
                </Badge>
              )}
            </div>
            {/* Icon actions */}
            <div className='tx-icon-actions'>
              {actions.map((a, i) => (
                <ActionIcon
                  key={i}
                  variant={a.variant}
                  icon={a.icon}
                  label={a.label}
                  onClick={() => {
                    if (a.label === "View store") {
                      navigate(`/mystores/ ${tx.store.id.storeName} /${tx.store.id._id}`);

                      onClose();
                      return;
                    }

                    a.fn();
                  }}
                />
              ))}
            </div>
          </div>

          {/* Store details */}
          <div className='tx-sheet-section'>
            <div className='tx-section-title'>
              <Store size={14} />
              Store details
            </div>
            <div className='tx-store-card'>
              <div className='tx-store-card-name'>{s.storeName || tx.store.name}</div>
              <div className='tx-store-card-meta'>
                {s.category && (
                  <span>
                    <Package size={11} />
                    {s.category}
                  </span>
                )}
                {s.storeAddress && (
                  <span>
                    <MapPin size={11} />
                    {s.storeAddress}
                  </span>
                )}
                {s.isStoreVerified ? (
                  <span style={{ color: "#2563eb" }}>
                    <BadgeCheck size={11} />
                    Verified
                  </span>
                ) : null}
                {
                  <span>
                    <Star size={11} />
                    {s.averageRating ? s.averageRating : "no rating"}
                  </span>
                }
              </div>
              <div className='tx-store-card-grid'>
                {[
                  ["Mode", s.superMarketMode ? "Supermarket" : "Retail"],

                  ["VAT rate", s.isVATenabled ? `${s.vatPercentage?.$numberDecimal}%` : "Disabled"],
                  ["Target sales", s.targetSales ? fmtC(s.targetSales, currency) : "—"],
                  ["Currency", s.currency || "NGN"],
                ].map(([l, v]) => (
                  <div key={l} className='tx-store-card-cell'>
                    <div className='tx-store-card-cell-label'>{l}</div>
                    <div className='tx-store-card-cell-val'>{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Loan overview */}
          {isL && (
            <div className='tx-sheet-section'>
              <div className='tx-section-title'>
                <Wallet size={14} />
                Loan overview
              </div>
              <DetailRow label='Initial payment' value={fmt(tx.operationType.intialPayment || 0, currency)} />
              <DetailRow label='Cleared amount' value={fmt(tx.operationType.clearedAmount || 0, currency)} valueStyle={{ color: "rgb(12, 134, 49)" }} />
              <DetailRow label='Balance remaining' value={fmt(tx.operationType.balance || 0, currency)} valueStyle={{ color: "#a82318" }} />
              <DetailRow label='Due date' value={tx.operationType.dateToPay || "—"} />
              <div className='tx-loan-progress-track'>
                <div className='tx-loan-progress-fill' style={{ width: `${pct}%` }} />
              </div>
              <div className='tx-loan-progress-labels'>
                <span>{pct}% paid</span>
                <span>{fmt(tx.operationType.balance || 0, currency)} remaining</span>
              </div>
            </div>
          )}

          {/* Transaction */}
          <div className='tx-sheet-section'>
            <div className='tx-section-title'>
              <Banknote size={14} />
              Transaction
            </div>
            <DetailRow label='Trans ID' value={truncateID(tx.transID)} mono />
            <DetailRow
              label='Date & time'
              value={new Date(d.date).toLocaleString("en-NG", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
            />
            <DetailRow label='Operation' value={isL ? "Loan / Credit sale" : "Cash sale"} />
            <DetailRow label='Sold by' value={tx.soldBy.fullName || "—"} />
            <DetailRow label='Items' value={`${tx.products.length} lines · ${d.numberOfItems} units`} />
          </div>

          {/* Payments — shows every account a transaction was paid
              through, whether it's a single payment or a split across
              multiple accounts. */}
          <div className='tx-sheet-section'>
            <div className='tx-section-title'>
              <CreditCard size={14} />
              Payments
              {d.paymentMode === "split" && d.payments?.length > 0 && <span className='tx-payments-count'>{d.payments.length} accounts</span>}
            </div>

            {d.payments && d.payments.length > 0 ? (
              <>
                <div className='tx-payments-list'>
                  {d.payments.map((p, i) => (
                    <div className='tx-payment-row' key={p._id || i}>
                      <PaymentTypeTag type={p.paymentType} />
                      <div className='tx-payment-row-info'>
                        <span className='tx-payment-row-name'>{p.receivingAccountName || "—"}</span>
                        {(p.reference || p.paidAt) && (
                          <span className='tx-payment-row-meta'>
                            {p.reference ? `Ref: ${p.reference}` : ""}
                            {p.reference && p.paidAt ? " · " : ""}
                            {p.paidAt ? new Date(p.paidAt).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" }) : ""}
                          </span>
                        )}
                        {p.remarks && <span className='tx-payment-row-remarks'>{p.remarks}</span>}
                      </div>
                      <span className='tx-payment-row-amount'>{fmt(p.amount, currency)}</span>
                    </div>
                  ))}
                </div>
                {d.payments.length > 1 && (
                  <div className='tx-payments-total'>
                    <span className='label'>Total paid</span>
                    <span className='value'>
                      {fmt(
                        d.payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
                        currency,
                      )}
                    </span>
                  </div>
                )}
              </>
            ) : (
              // Legacy transactions without a payments[] array yet — fall
              // back to the single paymentMode label.
              <DetailRow
                label='Payment method'
                value={
                  {
                    cash: "Cash",
                    card: "Card",
                    mobilePay: "Mobile Pay",
                    bankTransfer: "Bank Transfer",
                    POS: "POS",
                    storelensePOS: "StoreLense POS",
                    split: "Split",
                  }[d.paymentMode] || d.paymentMode
                }
              />
            )}
          </div>

          {/* Customer */}
          <div className='tx-sheet-section'>
            <div className='tx-section-title'>
              <User size={14} />
              Customer
            </div>
            <DetailRow label='Name' value={tx.customer.fullName || "Anonymous Customer"} />
            <DetailRow label='Phone' value={tx.customer.phoneNumber || "Not provided"} />
            {tx.customer.email && <DetailRow label='Email' value={tx.customer.email} />}
            {tx.customer.address && <DetailRow label='Address' value={tx.customer.address} />}
          </div>

          {/* Products */}
          <div className='tx-sheet-section'>
            <div className='tx-section-title'>
              <Package size={14} />
              Items purchased
            </div>
            {tx.products.map((p, i) => {
              const profit = (p.soldUnitPrice - p.productCostPrice) * p.quantity;
              return (
                <div key={i} className='tx-product-row'>
                  <div className='tx-product-icon'>
                    {p.productImage ? (
                      <img src={p.productImage} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <ProdAvatar name={p.name} qty={p.quantity} />
                    )}
                  </div>
                  <div className='tx-product-info'>
                    <div className='tx-product-name'>{p.name}</div>
                    <div className='tx-product-sub'>
                      {p.category} · ×{p.quantity} {p.unit} @ {fmt(p.soldUnitPrice, currency)}
                    </div>
                    <div className='tx-product-profit'>
                      <TrendingUp size={11} />
                      Profit {fmt(profit, currency)}
                    </div>
                    {isL ? null : (
                      <div className='tx-product-actions'>
                        {p.returned ? (
                          <span style={{ backgroundColor: "rgb(215, 215, 215)", color: "rgb(92, 92, 92)", border: "none" }} className='tx-returned-tag'>
                            Item Returned
                          </span>
                        ) : (
                          <button
                            className='tx-return-btn'
                            onClick={async () => {
                              try {
                                setIsReturningOne(i);
                                await onReturnItem(tx.transID, i);
                              } catch (e) {
                              } finally {
                                setIsReturningOne(null);
                              }
                            }}
                            disabled={isReturningOne === i}
                          >
                            {isReturningOne === i ? (
                              <ButtonLoaderII size={12} color='gray' />
                            ) : (
                              <>
                                <CornerUpLeft size={11} />
                                Return item
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className='tx-product-amount'>
                    <div className='total'>{fmt(p.totalAmount, currency)}</div>
                    <div className='sub'>subtotal</div>
                  </div>
                </div>
              );
            })}
          </div>
          {/*  {Number(selectedTrans.transactionDetails.vatPercentage?.toString()) || selectedTrans.transactionDetails.vatPercentage?.$numberDecimal}% */}

          {/* Breakdown */}
          <div className='tx-sheet-section'>
            <div className='tx-section-title'>
              <BarChart2 size={14} />
              Breakdown
            </div>
            <DetailRow label='Subtotal' value={fmt(d.subTotal, currency)} />

            {d.discount > 0 && <DetailRow label='Discount' value={`−${fmt(d.discount, currency)}`} valueStyle={{ color: "#156b43" }} />}

            {d.vat > 0 && <DetailRow label={`VAT (${d.vatPercentage?.$numberDecimal}%)`} value={fmt(d.vat, currency)} />}

            {d.serviceCharge > 0 && <DetailRow label='Service charge' value={fmt(d.serviceCharge, currency)} />}

            {d.deliveryFee > 0 && <DetailRow label='Delivery fee' value={fmt(d.deliveryFee, currency)} />}

            {d.clearedbalance > 0 && <DetailRow label='Extra payment (Off record debt cleared)' value={fmt(d.clearedbalance, currency)} />}

            {d.extraPayment > 0 && <DetailRow label='Extra payment' value={fmt(d.extraPayment, currency)} />}

            <div className='tx-breakdown-total'>
              <span className='label'>Total</span>
              <span className='value'>{fmt(d.totalAmount, currency)}</span>
            </div>
          </div>

          {/* Comment */}
          {tx.comments && (
            <div className='tx-sheet-section'>
              <div className='tx-section-title'>
                <BadgeCheck size={14} />
                Comment
              </div>
              <p style={{ fontSize: 13, color: "#4a6580", lineHeight: 1.6 }}>{tx.comments}</p>
            </div>
          )}
        </div>
      </div>

      {openClearLoanModal && (
        <ClearLoanModal
          isOpen={openClearLoanModal}
          amount={tx.operationType?.balance}
          onCancel={() => {
            setOpenClearLoanModal(false);
          }}
          onConfirm={async ({ amount, paymentMode, payments }) => {
            console.log("clear loan modal confirm", amount, paymentMode, payments);

            return await clearLoan(payments, paymentMode, amount);
          }}
          loanPaymentMode={loanPaymentMode}
          setLoanPaymentMode={setLoanPaymentMode}
          currency={currency}
          pushToTerminal={async (entry) => {
            return await pushToTerminal(entry);
          }}
          socket={userSocket}
        />
      )}

      {openExtendLoanModal && (
        <ExtendLoanModal
          isOpen={openExtendLoanModal}
          dueDate={tx.operationType.dateToPay}
          onCancel={() => {
            setOpenExtendLoanModal(false);
          }}
          onConfirm={async () => {
            return await extendLoanFun();
          }}
          newExtendDate={newExtendDate}
          setNewExtendDate={setNewExtendDate}
        />
      )}

      {openSplitPaymentModal && (
        <RegisterPaymentModal
          isOpen={openSplitPaymentModal}
          balance={tx.operationType?.balance}
          onCancel={() => {
            setOpenSplitPaymentModal(false);
          }}
          onConfirm={async ({ splitPaymentAmount, loanPaymentMode, payment }) => {
            return await splitPaymentFun(splitPaymentAmount, loanPaymentMode, payment);
          }}
          splitPaymentAmount={splitPaymentAmount}
          setSplitPaymentAmount={setSplitPaymentAmount}
          loanPaymentMode={loanPaymentMode}
          setLoanPaymentMode={setLoanPaymentMode}
          currency={currency}
          pushToTerminal={async (entry) => {
            return await pushToTerminal(entry);
          }}
          socket={userSocket}
          transID={tx.transID}
        />
      )}

      {showReceipt && <InvoicePDF invoiceData={tx} />}
    </>
  );
}

function DetailRow({ label, value, mono, valueStyle }) {
  return (
    <div className='tx-detail-row'>
      <span className='tx-dl'>{label}</span>
      <span className={`tx-dv${mono ? " mono" : ""}`} style={valueStyle}>
        {value}
      </span>
    </div>
  );
}

/* ─────────────────── Table view ─────────────────── */
function TableView({ txList, onOpenSheet, totalTx, currency }) {
  /*   const [page, setPage] = useState(1);
  const PER = 10;
  const pages = Math.max(1, Math.ceil(txList.length / PER)); */
  const slice = txList;

  /* useEffect(() => {
    if (page > pages) setPage(1);
  }, [txList.length]); */

  if (!txList.length) return <EmptyState />;

  return (
    <div className='tx-table-wrap'>
      <div className='tx-table-scroll'>
        <table className='tx-table'>
          <thead>
            <tr>
              <th className='tx-col-prod'>Product</th>
              <th className='tx-col-store'>Store</th>
              <th className='tx-col-items center'>Items</th>
              <th className='tx-col-total right'>
                Total <ChevronsUpDown size={10} style={{ opacity: 0.4, verticalAlign: "middle" }} />
              </th>
              <th className='tx-col-vat right'>VAT</th>
              <th className='tx-col-pay'>Payment</th>
              <th className='tx-col-op'>Operation</th>
              <th className='tx-col-del'>Delivery</th>
              <th className='tx-col-by'>Sold by</th>
              <th className='tx-col-date'>
                Date <ChevronsUpDown size={10} style={{ opacity: 0.4, verticalAlign: "middle" }} />
              </th>
            </tr>
          </thead>
          <tbody>
            {slice.map((tx) => {
              const d = tx.transactionDetails;
              const isL = tx.operationType.whichOperation === "loan";
              const s = tx.store.id || {};
              {
                /* const s = STORES[tx.store.id] || {}; */
              }
              const firstProd = tx.products[0] || {};
              const extra = tx.products.length > 1 ? tx.products.length - 1 : 0;
              const ts = d.date
                .toLocaleString("en-NG", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
                .replace(",", "");
              const isOverdue = isL && tx.operationType.dateToPay && new Date(tx.operationType.dateToPay) < new Date() && (tx.operationType.balance || 0) > 0;
              const isCleared = isL && (tx.operationType.balance || 0) <= 0;
              const daysDiff = isOverdue ? Math.floor((new Date() - new Date(tx.operationType.dateToPay)) / 86400000) : 0;
              const [bg, fg] = avatarColors(firstProd.name || "?");

              return (
                <tr key={tx.transID} onClick={() => onOpenSheet(tx.transID)}>
                  <td>
                    <div className='tx-prod-cell'>
                      <ProdAvatar name={firstProd.name} />
                      <div style={{ minWidth: 0 }}>
                        <div className='tx-prod-name'>
                          {firstProd.name || "—"}
                          {extra > 0 && <span className='tx-extra-prods'> +{extra} more</span>}
                        </div>
                        <div className='tx-prod-customer'>
                          <User size={10} />
                          {tx.customer.fullName || "Anonymous"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className='tx-store-name'>{tx.store.name}</div>
                    <div className='tx-store-cat'>{s.category || ""}</div>
                  </td>
                  <td className='center'>{d.numberOfItems}</td>
                  <td className='right'>
                    <span className='tx-total-val'>{fmt(d.totalAmount, currency)}</span>
                  </td>
                  <td className='right'>{d.vat > 0 ? fmt(d.vat, currency) : <span className='tx-vat-nil'>—</span>}</td>
                  <td>
                    <PaymentBadge mode={d.paymentMode} />
                  </td>
                  <td>
                    <div className='tx-op-cell'>
                      {isL ? (
                        <Badge variant='loan' dot>
                          Loan
                        </Badge>
                      ) : (
                        <Badge variant='cash'>
                          <Banknote size={11} />
                          Cash
                        </Badge>
                      )}
                      {isOverdue && (
                        <Badge variant='overdue' dot>
                          {daysDiff}d overdue
                        </Badge>
                      )}
                      {isCleared && (
                        <div
                          style={{
                            background: "rgba(67, 21, 128, 0.8)",
                            color: "#fff",
                            fontSize: 10,
                            padding: "2px 8px",
                            borderRadius: 20,
                            display: "inline-block",
                          }}
                        >
                          paid up
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <DeliveryBadge status={d.deliveryStatus} />
                  </td>
                  <td>
                    <div className='tx-sold-by'>
                      <UserCheck size={13} />
                      {tx.soldBy?.fullName || "Admin"}
                    </div>
                  </td>
                  <td>
                    <span className='tx-date-val'>{ts}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className='tx-table-footer'>
        <span>
          <strong>{txList.length.toLocaleString()}</strong> result{txList.length !== 1 ? "s" : ""} of <strong>{Number(totalTx || 3).toLocaleString()}</strong>
        </span>
        {/* <div className='tx-pagination'>
          <button className='tx-page-btn' onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
            <ChevronLeft size={14} />
          </button>
          {Array.from({ length: pages }, (_, i) => i + 1).map((i) => (
            <button key={i} className={`tx-page-btn ${i === page ? "active" : ""}`} onClick={() => setPage(i)}>
              {i}
            </button>
          ))}
          <button className='tx-page-btn' onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page === pages}>
            <ChevronRight size={14} />
          </button>
        </div> */}
      </div>
    </div>
  );
}

/* ─────────────────── Cards view ─────────────────── */
function CardsView({ txList, onOpenSheet, onMarkSupplied, currency }) {
  if (!txList.length) return <EmptyState />;

  return (
    <div className='tx-cards-wrap'>
      {txList.map((tx) => {
        const d = tx.transactionDetails;
        const isL = tx.operationType.whichOperation === "loan";
        const s = tx.store.id || {};
        {
          /* const s = STORES[tx.store.id] || {}; */
        }
        console.log(tx);
        const profit = tx.products.reduce((a, p) => a + (p.soldUnitPrice - p.productCostPrice) * p.quantity, 0);
        const time = d.date
          ? new Date(d.date).toLocaleTimeString("en-NG", {
              hour: "2-digit",
              minute: "2-digit",
            })
          : "";
        const pct =
          isL && d.totalAmount ? Math.round((((tx.operationType.intialPayment || 0) + (tx.operationType?.clearedAmount || 0)) / d.totalAmount) * 100) : 0;
        const unD = ["notSupplied", "pending"].includes(d.deliveryStatus);
        const displayProds = tx.products.slice(0, 2);
        const extraProds = tx.products.length > 2 ? tx.products.length - 2 : 0;

        return (
          <div key={tx.transID} className='tx-card' onClick={() => onOpenSheet(tx.transID)}>
            <div className='tx-card-head'>
              <div className='tx-card-top'>
                <div className='tx-card-products'>
                  {displayProds.map((p, i) => {
                    const [bg, fg] = avatarColors(p.name);
                    return (
                      <div key={i} className='tx-card-prod-row'>
                        <div className='tx-card-avatar' style={{ background: bg, color: fg }}>
                          {p.quantity > 1 ? p.quantity : p.name.charAt(0).toUpperCase()}
                        </div>
                        <span className='tx-card-prod-name'>{p.name}</span>
                      </div>
                    );
                  })}
                  {extraProds > 0 && <div className='tx-card-extra-chip'>+{extraProds} more</div>}
                </div>
                <div className='tx-card-amount-wrap'>
                  <div className='tx-card-amount'>{fmt(d.totalAmount, currency)}</div>
                  {profit > 0 && (
                    <div className='tx-card-profit'>
                      <TrendingUp size={10} />+{fmt(profit, currency)}
                    </div>
                  )}
                </div>
              </div>
              <div className='tx-card-txid'>{tx.transID}</div>
              <div className='tx-card-badges'>
                <PaymentBadge mode={d.paymentMode} />
                <DeliveryBadge status={d.deliveryStatus} />
                {isL && (
                  <Badge variant='loan' dot>
                    Loan
                  </Badge>
                )}
              </div>
            </div>

            <div className='tx-card-body'>
              <div className='tx-card-row'>
                <span className='tx-card-label'>
                  <User size={12} />
                  Customer
                </span>
                <div className='tx-card-value'>
                  <div>{tx.customer.fullName || "Anonymous"}</div>
                  <div style={{ fontSize: 11, color: "#8da4b8" }}>{tx.customer.phoneNumber || "No phone"}</div>
                </div>
              </div>
              <div className='tx-card-row'>
                <span className='tx-card-label'>
                  <Store size={12} />
                  Store
                </span>
                <span className='tx-card-store-val'>{tx.store.name}</span>
              </div>
              {d.vat > 0 && (
                <div className='tx-card-row'>
                  <span className='tx-card-label'>
                    <CreditCard size={12} />
                    VAT
                  </span>
                  <span className='tx-card-value'>{fmt(d.vat, currency)}</span>
                </div>
              )}
            </div>

            {isL && tx.operationType.balance > 0 && (
              <div className='tx-loan-bar'>
                <div className='tx-loan-track'>
                  <div className='tx-loan-fill' style={{ width: `${pct}%` }} />
                </div>
                <span className='tx-loan-meta'>
                  {pct}% paid · {fmt(tx.operationType.balance, currency)} left
                </span>
              </div>
            )}
            {isL && tx.operationType.balance === 0 && (
              <div className='tx-loan-bar'>
                <div className='tx-loan-track'>
                  <div className='tx-loan-fill' style={{ width: `${pct}%` }} />
                </div>
                <span className='tx-loan-meta'>
                  {pct}% paid · {fmt(tx.operationType.balance, currency)} left
                </span>
              </div>
            )}

            <div className='tx-card-footer'>
              <span className='tx-card-by'>
                <User size={12} />
                {tx.soldBy.fullName}
              </span>
              <span className='tx-card-time'>{time}</span>
            </div>

            {unD && (
              <div className='tx-card-ctas' onClick={(e) => e.stopPropagation()}>
                {/*    {isL && tx.operationType.balance > 0 && tx.transactionDetails.deliveryStatus !== "returned" && (
                  <>
                    <button className='tx-cta-chip tx-cta-navy'>
                      <Wallet size={12} />
                      Split pay
                    </button>
                    <button className='tx-cta-chip tx-cta-green'>
                      <Check size={12} />
                      Clear
                    </button>
                    <button className='tx-cta-chip tx-cta-amber'>
                      <Clock size={12} />
                      Extend
                    </button>
                  </>
                )} */}
                {/*  {unD && (
                  <button
                    className='tx-cta-chip tx-cta-green'
                    onClick={async (e) => {
                      try {
                        setLoading(true);
                        await onMarkSupplied(tx.transID);
                      } catch (e) {
                        console.error(e);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                  >
                    {loading ? (
                      <ButtonLoaderII size={12} />
                    ) : (
                      <>
                        <PackageCheck size={12} />" Mark supplied"
                      </>
                    )}
                  </button>
                )} */}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─────────────────── Empty state ─────────────────── */
function EmptyState() {
  return (
    <div className='tx-empty'>
      <Banknote size={42} style={{ opacity: 0.35, marginBottom: 12 }} />
      <h3>No transactions found</h3>
      <p>Try adjusting your filters or search term</p>
    </div>
  );
}

/* ─────────────────── Main component ─────────────────── */
export default function Transactions({ userSocket }) {
  const [txData, setTxData] = useState([]);
  const [txDataCopy, setTxDataCopy] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ delivery: "", payment: "", operation: "" });
  const [period, setPeriod] = useState("All time");
  const [loansOnly, setLoansOnly] = useState(false);
  const [view, setView] = useState(window.innerWidth <= 768 ? "cards" : "table");
  const [selectedTx, setSelectedTx] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [showPeriod, setShowPeriod] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [toast, setToast] = useState("");
  const [returnedMap, setReturnedMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [returning, setReturning] = useState(false);

  const { user } = useSelector((state) => state.webStoreUser);
  const currency = user.userCountryCurrencySymbol || "#";

  // Auto switch on resize
  useEffect(() => {
    const handler = () => {
      const should = window.innerWidth <= 768;
      setView((v) => (should ? "cards" : "table"));
    };
    fetchTransactions();
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Toast helper
  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2400);
  }, []);

  const fetchTransactions = async (pageNo = 1) => {
    try {
      setLoading(true);
      const limit = 100;

      const url = fetchUsersTransactionUrl + user._id + "/?page=" + pageNo + `&limit=${limit}`;

      let response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + localStorage.getItem("accessToken"),
        },
      });

      if (response.status === 404) {
        uiAlert("No transactions found.");
        setTxData([]);
        return;
      }

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      let { transactions, pagination } = await response.json();
      console.log("Fetched transactions:", pagination);

      setTxData(transactions);
      setTotalPages(pagination?.totalPages || 1);
      setPage(pagination?.page || 1);
      setTotalTransactions(pagination?.total || transactions.length);
      setTxDataCopy(transactions);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      uiAlert("Failed to fetch transactions. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const range = getDateRangeForPeriod(period);

    console.log("Filtering transactions:", range, txDataCopy.length);

    if (!range) {
      setTxData(txDataCopy);
      return;
    }

    const [start, end] = range;

    const filtered = txDataCopy?.filter((tx) => {
      const txDate = new Date(tx.transactionDetails.date);

      return txDate >= start && txDate <= end;
    });

    setTxData(filtered);
  }, [period, txDataCopy]);
  // Filter
  const filteredTx = useMemo(() => {
    return txData?.filter((tx) => {
      if (loansOnly && (tx.operationType.whichOperation !== "loan" || tx.operationType.balance <= 0 || tx.transactionDetails.deliveryStatus === "returned"))
        return false;
      if (filters.delivery && tx.transactionDetails.deliveryStatus !== filters.delivery) return false;
      if (filters.payment && tx.transactionDetails.paymentMode !== filters.payment) return false;
      if (filters.operation && tx.operationType.whichOperation !== filters.operation) return false;
      if (search) {
        const hay = [tx.transID, tx.customer.fullName, tx.store.name, tx.customer.phoneNumber].join(" ").toLowerCase();
        if (!hay.includes(search.toLowerCase())) return false;
      }
      return true;
    });
  }, [txData, search, filters, loansOnly]);

  const hasFilters = !!(filters.delivery || filters.payment || filters.operation);

  const openSheet = (id) => {
    const tx = txData.find((t) => t.transID === id);
    if (!tx) return;
    // apply returned map
    const rm = returnedMap[id] || {};
    const merged = { ...tx, products: tx.products.map((p, i) => ({ ...p, returned: p.returned || !!rm[i] })) };
    setSelectedTx(merged);
  };

  const markSupplied = async (id) => {
    try {
      const payload = {
        transactionID: id,
      };

      const url = supplyAllProductUrl + selectedTx.store.id._id;

      let response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + localStorage.getItem("accessToken"),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to mark as supplied");
      }

      let { transaction } = await response.json();
      setSelectedTx(transaction);

      setTxData((prev) => prev.map((t) => (t.transID !== id ? t : { ...t, transactionDetails: { ...t.transactionDetails, deliveryStatus: "supplied" } })));

      showToast("Marked as supplied ✓");

      return true;
    } catch (e) {
      uiAlert(e.message || "Failed to mark as supplied. Please try again later.", "error");
      return false;
    }
  };

  const returnItem = async (txId, idx) => {
    /*  uiAlert("To return this item, please login to the store where the purchase was made.", "info");

    return; */
    const product = selectedTx?.products?.[idx];

    if (!product) {
      uiAlert("Product not found.", "error");
      return;
    }

    if (product.returned) {
      uiAlert("This item has already been returned.", "info");
      return;
    }

    if (returning) {
      uiAlert("Return in progress. Please wait.", "info");
      return;
    }

    if (!window.confirm(`Are you sure you want to return ${product.name}?`)) {
      return;
    }

    setReturning(true);

    try {
      let payload = {
        transactionID: txId,
        productID: product.id,
        customer: selectedTx.customer.fullName,
        subUnitID: product.isSubUnitSold ? product.subUnitId : null,
      };

      const url = returnOneProductUrl + selectedTx.store.id._id;

      let response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + localStorage.getItem("accessToken"),
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to return item");
      }

      let { transaction, returnedProduct, updatedStoreProductCollection, store, foundProduct } = await response.json();

      setSelectedTx(transaction);

      /*  setReturnedMap((prev) => ({ ...prev, [txId]: { ...(prev[txId] || {}), [idx]: true } })); */
      // re-open to refresh
      /* const tx = txData.find((t) => t.transID === txId); */
      /*    if (tx) {
      const rm = { ...(returnedMap[txId] || {}), [idx]: true };
      setSelectedTx({ ...tx, products: tx.products.map((p, i) => ({ ...p, returned: p.returned || !!rm[i] })) });
    } */

      // replace the transaction in the main list
      setTxData((prev) => prev.map((t) => (t.transID !== txId ? t : transaction)));

      showToast("Item return recorded ↩");

      uiAlert(`${foundProduct.quantity} ${returnedProduct.unit} of ${returnedProduct.name} returned successfully.`, "success");

      return true;
    } catch (e) {
      console.error("Error returning item:", e);
      uiAlert(e.message || "Failed to return item. Please try again later.", "error");
      return false;
    } finally {
      setReturning(false);
    }
  };

  {
    /*   <TransactionsSkeleton rows={10} cards={6} /> */
  }

  return (
    <Template>
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "calc(100vh - 160px)" }}>
          <Loader size={70} />
        </div>
      ) : (
        <div className='tx-page'>
          {/* Topbar */}

          <div className='tx-page-body'>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
              <div className='tx-page-head'>
                <h1>Transactions</h1>
                <p>All store sales transactions across your network</p>
              </div>
              <div>
                <button className='tx-topbar-btn' onClick={() => setShowInsights(true)}>
                  <BarChart2 size={15} />
                  Insights
                </button>
              </div>
            </div>
            {/* Period bar */}
            <div className='tx-period-bar'>
              <button className='tx-period-btn' onClick={() => setShowPeriod(true)}>
                <Store size={14} />
                {period}
                <ChevronDown size={13} />
              </button>
              <button className={`tx-loans-btn ${loansOnly ? "active" : ""}`} onClick={() => setLoansOnly((v) => !v)}>
                <Wallet size={14} />
                All active credits{loansOnly ? " ✓" : ""}
              </button>
            </div>
            {/* Toolbar */}
            <div className='tx-toolbar'>
              <div className='tx-search'>
                <Search size={15} />
                <input type='text' placeholder='Trans ID, store, customer…' value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <button className={`tx-filter-btn ${hasFilters ? "active" : ""}`} onClick={() => setShowFilter(true)}>
                <SlidersHorizontal size={17} />
                {hasFilters && <span className='tx-filter-dot' />}
              </button>
              <div className='tx-toolbar-sep' />
              <div className='tx-toolbar-right'>
                <div className='tx-view-toggle'>
                  {/*  <button className={`tx-view-btn table-only ${view === "table" ? "active" : ""}`} onClick={() => setView("table")}>
                  <Table2 size={14} />
                  Table
                </button>
                <button className={`tx-view-btn ${view === "cards" ? "active" : ""}`} onClick={() => setView("cards")}>
                  <LayoutGrid size={14} />
                  Cards
                </button> */}
                </div>

                {/*  <button className='tx-export-btn' onClick={() => showToast("Exporting CSV…")}>
                  <Download size={15} />
                  Export
                </button> */}
              </div>
            </div>
            {/* Table / Cards */}
            {view === "table" ? (
              <TableView txList={filteredTx} onOpenSheet={openSheet} totalTx={totalTransactions} currency={currency} />
            ) : (
              <CardsView txList={filteredTx} onOpenSheet={openSheet} onMarkSupplied={markSupplied} totalTx={totalTransactions} currency={currency} />
            )}

            <Pagination current={page} total={totalPages} onChange={fetchTransactions} />
          </div>

          {/* Overlays */}
          {showFilter && <FilterOverlay filters={filters} onChange={setFilters} onClose={() => setShowFilter(false)} currency={currency} />}
          {showPeriod && <PeriodOverlay active={period} onSelect={setPeriod} onClose={() => setShowPeriod(false)} currency={currency} />}
          {showInsights && <InsightsOverlay txList={filteredTx} period={period} onClose={() => setShowInsights(false)} currency={currency} />}
          {selectedTx && (
            <DetailSheet
              tx={selectedTx}
              updateSelectedTx={setSelectedTx}
              onClose={() => setSelectedTx(null)}
              onMarkSupplied={markSupplied}
              onReturnItem={returnItem}
              currency={currency}
              setTxData={setTxData}
              userSocket={userSocket}
            />
          )}

          <Toast message={toast} />
        </div>
      )}
    </Template>
  );
}
